import * as React from 'react';
import './style.scss';
import { dateSort, isDateString } from '../../utils/utils';

interface TableProps {
    data: Array<{ [key: string]: any }>;
    columns: Array<{ key: string; label: string, renderer?: (data: any) => any }>;
    itemsPerPage?: number;
    title?: string | React.ReactNode;
}

export const TableComponent: React.FC<TableProps> = ({ data, columns, itemsPerPage = 5, title }) => {
    const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' | null } | null>(null);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [searchText, setSearchText] = React.useState('');

    // Filter data based on search text
    const filteredData = React.useMemo(() => {
        if (!searchText.trim()) {
            return data;
        }

        const lowerSearchText = searchText.toLowerCase();
        return data.filter((row) => {
            // Search across all columns
            return columns.some((col) => {
                const cellValue = row[col.key];
                if (cellValue === null || cellValue === undefined) {
                    return false;
                }
                return cellValue.toString().toLowerCase().includes(lowerSearchText);
            });
        });
    }, [data, searchText, columns]);

    // Sorting
    const sortedData = [...filteredData].sort((a, b) => {
        return sortConfig?.direction ? dateSort(sortConfig.direction, a[sortConfig.key], b[sortConfig.key]) : 0;
    });

    // Pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentData = sortedData.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev && prev.key === key) {
                const direction = prev.direction === 'asc' ? 'desc' : 'asc';
                return { key, direction };
            }
            return { key, direction: 'asc' };
        });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleClearSearch = () => {
        setSearchText('');
        setCurrentPage(1);
    };

    return (
        <div>
            {/* Header with Title and Search Filter */}
            {title && (
                <div className="table-header">
                    <div className="table-title">{title}</div>
                    <div className="table-search-filter">
                <div className="search-input-wrapper">
                    <i className='bx bx-search-alt-2'></i>
                    <input
                        type="text"
                        placeholder="Filter table..."
                        value={searchText}
                        onChange={handleSearchChange}
                        className="search-input"
                    />
                    {searchText && (
                        <i 
                            className='bx bx-x'
                            onClick={handleClearSearch}
                            style={{ cursor: 'pointer' }}
                        ></i>
                    )}
                </div>
                {searchText && (
                    <div className="search-result-count">
                        {filteredData.length} result{filteredData.length !== 1 ? 's' : ''} found
                    </div>
                )}
                    </div>
                </div>
            )}
            {!title && (
                <div className="table-search-filter">
                    <div className="search-input-wrapper">
                        <i className='bx bx-search-alt-2'></i>
                        <input
                            type="text"
                            placeholder="Filter table..."
                            value={searchText}
                            onChange={handleSearchChange}
                            className="search-input"
                        />
                        {searchText && (
                            <i 
                                className='bx bx-x'
                                onClick={handleClearSearch}
                                style={{ cursor: 'pointer' }}
                            ></i>
                        )}
                    </div>
                    {searchText && (
                        <div className="search-result-count">
                            {filteredData.length} result{filteredData.length !== 1 ? 's' : ''} found
                        </div>
                    )}
                </div>
            )}
            <table className='tableComponent'>
                <thead>
                    <tr>
                        {columns.map((col) => (
                        <th
                            key={col.key}
                            onClick={() => handleSort(col.key)}
                            className={(sortConfig?.key === col.key && sortConfig.direction) ? 'active' : undefined}
                        >
                            {col.label} {sortConfig?.key === col.key && (sortConfig.direction === 'asc' ? <i className='bx bx-chevron-up green' ></i> : <i className='bx bx-chevron-down green' ></i>)}
                        </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {currentData.map((row, index) => (
                        <tr key={index} >
                        {columns.map((col) => (
                            <td key={col.key} >
                            {col.renderer ? col.renderer(row) : row[col.key]}
                            </td>
                        ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            { totalPages === 0 ? <p style={{textAlign: 'center', marginTop: '20px', color: 'var(--text-secondary)'}}>
                {searchText ? 'No records match your search' : 'No Records'}
            </p> : null }
            { totalPages > 0 && (!(currentPage === 1) || !(currentPage === totalPages)) ? (<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{ padding: '5px 10px', cursor: 'pointer' }}
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{ padding: '5px 10px', cursor: 'pointer' }}
                >
                    Next
                </button>
            </div>) : null }
        </div>
    );
};

